import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  PencilSquareIcon,
  LockClosedIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logoutUser, updateUserProfile, changePassword } from '../../store/authSlice';
import { useForm } from 'react-hook-form';
import { Button, Chip, Input, Modal } from '../ui';

const UserMenu = ({ anchorEl, open, onClose }) => {
  const dispatch = useAppDispatch();
  const { user, isLoading } = useAppSelector((state) => state.auth);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    reset: resetProfile,
  } = useForm();

  // Update form when user changes or dialog opens
  React.useEffect(() => {
    if (profileDialogOpen && user) {
      resetProfile({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        phone: user?.mobile_no || '',
      });
    }
  }, [profileDialogOpen, user, resetProfile]);

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    watch,
    reset: resetPassword,
  } = useForm();

  const password = watch('new_password');

  // Position the dropdown just below the anchor, right-aligned
  // (MUI Menu anchorOrigin bottom/right + transformOrigin top/right).
  useLayoutEffect(() => {
    if (open && anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: Math.max(window.innerWidth - rect.right, 8),
      });
    }
  }, [open, anchorEl]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        (!anchorEl || !anchorEl.contains(e.target))
      ) {
        onClose();
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, anchorEl, onClose]);

  const handleProfileUpdate = async (data) => {
    setIsUpdating(true);
    const result = await dispatch(updateUserProfile(data));
    setIsUpdating(false);

    if (updateUserProfile.fulfilled.match(result)) {
      setProfileDialogOpen(false);
      resetProfile();
      onClose();
    }
  };

  const handlePasswordChange = async (data) => {
    setIsUpdating(true);
    const result = await dispatch(changePassword({
      old_password: data.old_password,
      new_password: data.new_password,
    }));
    setIsUpdating(false);

    if (changePassword.fulfilled.match(result)) {
      setPasswordDialogOpen(false);
      resetPassword();
      onClose();
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    onClose();
  };

  const handleProfileClick = () => {
    setProfileDialogOpen(true);
    onClose();
  };

  const handlePasswordClick = () => {
    setPasswordDialogOpen(true);
    onClose();
  };

  const handleAccessClick = () => {
    setAccessDialogOpen(true);
    onClose();
  };

  const capabilityMap = user?.business_capabilities?.map || {};
  const capabilityPermissions = user?.business_capabilities?.permissions || {};
  const capabilityList = Object.keys(capabilityMap || {}).map((capability) => ({
    capability,
    actions: Array.isArray(capabilityMap?.[capability]) ? capabilityMap[capability] : [],
    canView:
      capabilityPermissions?.[`${capability}:view`] === true ||
      capabilityPermissions?.[`${capability}:report`] === true,
  }));
  const grantedCapabilities = capabilityList.filter((c) => c.canView);
  const deniedCapabilities = capabilityList.filter((c) => !c.canView);

  const menuItemClass =
    'flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#212B36] hover:bg-slate-100';

  return (
    <>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          className="fixed z-[1300] min-w-[200px] rounded-lg border border-[#E9EDF6] bg-white py-1 shadow-lg"
          style={{ top: menuPosition.top, right: menuPosition.right }}
        >
          <div className="px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#2e9e2e] text-base font-medium text-white">
                {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#212B36]">
                  {user?.full_name || user?.email}
                </p>
                <p className="truncate text-xs text-[#637381]">{user?.email}</p>
              </div>
            </div>
          </div>
          <hr className="border-[#E9EDF6]" />
          <button type="button" role="menuitem" className={menuItemClass} onClick={handleProfileClick}>
            <PencilSquareIcon className="h-4 w-4 text-[#637381]" />
            <span>Edit Profile</span>
          </button>
          <button type="button" role="menuitem" className={menuItemClass} onClick={handlePasswordClick}>
            <LockClosedIcon className="h-4 w-4 text-[#637381]" />
            <span>Change Password</span>
          </button>
          <button type="button" role="menuitem" className={menuItemClass} onClick={handleAccessClick}>
            <ShieldCheckIcon className="h-4 w-4 text-[#637381]" />
            <span>My Access</span>
          </button>
          <hr className="border-[#E9EDF6]" />
          <button type="button" role="menuitem" className={menuItemClass} onClick={handleLogout}>
            <ArrowRightOnRectangleIcon className="h-4 w-4 text-[#637381]" />
            <span>Logout</span>
          </button>
        </div>
      )}

      {/* Access Summary Dialog */}
      <Modal
        open={accessDialogOpen}
        onClose={() => setAccessDialogOpen(false)}
        maxWidth="md"
        title="My Access"
        footer={
          <Button variant="outline" onClick={() => setAccessDialogOpen(false)}>
            Close
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-bold text-[#212B36]">Access granted</p>
            {grantedCapabilities.length === 0 ? (
              <p className="text-sm text-[#637381]">No capabilities granted.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {grantedCapabilities.map((c) => (
                  <div
                    key={c.capability}
                    className="rounded-lg border border-[#E9EDF6] bg-white p-2.5"
                  >
                    <p className="text-sm font-bold text-[#212B36]">{c.capability}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {c.actions.map((a) => (
                        <Chip key={a} size="xs" label={a} color="success" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-bold text-[#212B36]">Access denied</p>
            {deniedCapabilities.length === 0 ? (
              <p className="text-sm text-[#637381]">No capabilities denied.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {deniedCapabilities.map((c) => (
                  <div
                    key={c.capability}
                    className="rounded-lg border border-dashed border-[#E9EDF6] bg-white p-2.5"
                  >
                    <p className="text-sm font-bold text-[#212B36]">{c.capability}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {c.actions.map((a) => (
                        <Chip key={a} size="xs" label={a} color="default" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Update Profile Dialog */}
      <Modal
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="sm"
        title="Update Profile"
        footer={
          <>
            <Button variant="outline" onClick={() => setProfileDialogOpen(false)} disabled={isUpdating}>
              Cancel
            </Button>
            <Button loading={isUpdating} onClick={handleProfileSubmit(handleProfileUpdate)}>
              Update
            </Button>
          </>
        }
      >
        <form id="profile-form" onSubmit={handleProfileSubmit(handleProfileUpdate)}>
          <div className="dialog-form-grid dialog-cols-2">
            <div>
              <Input
                label="First Name"
                {...registerProfile('first_name', {
                  required: 'First name is required',
                  minLength: { value: 2, message: 'Must be at least 2 characters' },
                })}
                error={!!profileErrors.first_name}
                helperText={profileErrors.first_name?.message}
                disabled={isUpdating}
              />
            </div>
            <div>
              <Input
                label="Last Name"
                {...registerProfile('last_name', {
                  required: 'Last name is required',
                  minLength: { value: 2, message: 'Must be at least 2 characters' },
                })}
                error={!!profileErrors.last_name}
                helperText={profileErrors.last_name?.message}
                disabled={isUpdating}
              />
            </div>
            <div className="dialog-col-span-full">
              <Input
                label="Phone Number"
                type="tel"
                {...registerProfile('phone', {
                  pattern: {
                    value: /^\+?[1-9]\d{1,14}$/,
                    message: 'Invalid phone number format',
                  },
                })}
                error={!!profileErrors.phone}
                helperText={profileErrors.phone?.message}
                disabled={isUpdating}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Change Password Dialog */}
      <Modal
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        title="Change Password"
        footer={
          <>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} disabled={isUpdating}>
              Cancel
            </Button>
            <Button loading={isUpdating} onClick={handlePasswordSubmit(handlePasswordChange)}>
              Change Password
            </Button>
          </>
        }
      >
        <form id="password-form" onSubmit={handlePasswordSubmit(handlePasswordChange)}>
          <div className="dialog-form-grid dialog-cols-2">
            <div className="dialog-col-span-full">
              <Input
                label="Current Password"
                type="password"
                {...registerPassword('old_password', {
                  required: 'Current password is required',
                })}
                error={!!passwordErrors.old_password}
                helperText={passwordErrors.old_password?.message}
                disabled={isUpdating}
              />
            </div>
            <div>
              <Input
                label="New Password"
                type="password"
                {...registerPassword('new_password', {
                  required: 'New password is required',
                  minLength: { value: 8, message: 'Must be at least 8 characters' },
                })}
                error={!!passwordErrors.new_password}
                helperText={passwordErrors.new_password?.message}
                disabled={isUpdating}
              />
            </div>
            <div>
              <Input
                label="Confirm New Password"
                type="password"
                {...registerPassword('confirm_password', {
                  required: 'Please confirm your password',
                  validate: (value) => value === password || 'Passwords do not match',
                })}
                error={!!passwordErrors.confirm_password}
                helperText={passwordErrors.confirm_password?.message}
                disabled={isUpdating}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default UserMenu;
